import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface LoadingStateProps {
  variant?: 'table' | 'detail' | 'form' | 'cards';
  rows?: number;
  density?: 'comfortable' | 'dense';
  className?: string;
}

const TABLE_CELL_WIDTHS = ['w-24', 'w-40', 'w-16', 'w-32', 'w-20'] as const;

export function LoadingState({
  variant = 'table',
  rows = 8,
  density = 'comfortable',
  className,
}: LoadingStateProps) {
  if (variant === 'form') {
    return (
      <div className={cn('space-y-6', className)} role="status" aria-label="Loading">
        {[0, 1].map((section) => (
          <div key={section} className="bg-surface rounded-lg border border-(--color-border) p-6">
            <Skeleton className="h-3 w-24 bg-neutral-200" />
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-3 w-20 bg-neutral-200" />
                  <Skeleton className="h-10 w-full bg-neutral-200" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'detail') {
    return (
      <div className={cn('space-y-6', className)} role="status" aria-label="Loading">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64 bg-neutral-200" />
          <Skeleton className="h-4 w-96 max-w-full bg-neutral-200" />
        </div>
        <Skeleton className="h-40 w-full rounded-lg bg-neutral-100" />
        <Skeleton className="h-40 w-full rounded-lg bg-neutral-100" />
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div
        className={cn(
          'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          className,
        )}
        role="status"
        aria-label="Loading"
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-xl bg-neutral-100" />
        ))}
      </div>
    );
  }

  // 'table' variant — renders bare skeleton rows at target density.
  const rowHeight = density === 'dense' ? 'h-9' : 'h-12';

  return (
    <div
      className={cn('divide-y divide-(--color-border)', className)}
      role="status"
      aria-label="Loading"
    >
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className={cn('flex items-center gap-4 px-4', rowHeight)}>
          {TABLE_CELL_WIDTHS.map((w, c) => (
            <Skeleton key={c} className={cn('h-3 bg-neutral-200', w)} />
          ))}
        </div>
      ))}
    </div>
  );
}
